(function () {
  const sections = document.querySelectorAll('.product-section');
  if (!sections.length) return;

  /* ─── Shared cart state ─────────────────────────────────────────── */
  /* Tracks variants in the cart split by purchase type. We only block
     "add as subscription" when the product is already in the cart as a
     subscription — one-time purchases can always be added (multi qty). */
  const CartState = {
    subscriptions: new Set(),    // variant IDs in cart with a selling_plan
    oneTimes: new Map(),         // variantId(number) -> { quantity, key }
    listeners: new Set(),

    async refresh() {
      try {
        const res = await fetch(`${window.Shopify.routes.root}cart.js`, {
          credentials: 'same-origin',
        });
        if (!res.ok) return;
        const cart = await res.json();
        const subs = new Set();
        const ones = new Map();
        cart.items.forEach((item) => {
          const isSubscription =
            item.selling_plan_allocation || item.selling_plan_id || item.properties?._selling_plan;
          if (isSubscription) {
            subs.add(item.variant_id);
          } else {
            ones.set(item.variant_id, { quantity: item.quantity, key: item.key });
          }
        });
        this.subscriptions = subs;
        this.oneTimes = ones;
        this.listeners.forEach((fn) => fn(this));
      } catch (err) {
        console.warn('CartState.refresh failed', err);
      }
    },

    onChange(fn) {
      this.listeners.add(fn);
      return () => this.listeners.delete(fn);
    },

    hasSubscription(variantId) {
      return this.subscriptions.has(parseInt(variantId, 10));
    },

    hasOneTime(variantId) {
      return this.oneTimes.has(parseInt(variantId, 10));
    },

    getOneTime(variantId) {
      return this.oneTimes.get(parseInt(variantId, 10)) || null;
    },
  };

  /* External cart mutations (cart drawer, mini-cart, etc.) should
     dispatch this event so we refresh our buttons */
  document.addEventListener('cart:updated', () => CartState.refresh());

  /* ─── Init each section ─────────────────────────────────────────── */
  function initSection(section) {
    if (section._productSectionInit) return;
    section._productSectionInit = true;
    initAccordion(section);
    initLightbox(section);
    initPurchaseOptions(section);
    initMainAtc(section);
    initFbt(section);
    initBundleSlider(section);
    initGallerySlider(section);
    initStickyAtc(section);
  }

  CartState.refresh().finally(() => {
    sections.forEach(initSection);
  });

  /* Re-initialise a re-rendered section. Both the Theme Editor and
     `shopify theme dev` hot reload swap in fresh markup that carries no
     listeners, and the browser will not re-run an external script it has
     already executed — without this the gallery, accordions, purchase
     options and add-to-cart all go dead until a full page reload. The
     shared CartState above is deliberately left alone: it is set up once
     per page and its `cart:updated` listener must not stack. */
  document.addEventListener('shopify:section:load', (event) => {
    const target = event.target;
    if (!target || !target.querySelectorAll) return;
    if (target.matches && target.matches('.product-section')) initSection(target);
    target.querySelectorAll('.product-section').forEach(initSection);
  });

  /* ─────────────────────────────────────────────────────────────────
     Accordion (Description / Ingredients)
     ───────────────────────────────────────────────────────────────── */
  function initAccordion(scope) {
    scope.querySelectorAll('.product-section__acc-item').forEach((details) => {
      const summary = details.querySelector('.product-section__acc-summary');
      const panel = details.querySelector('.product-section__acc-panel');
      if (!summary || !panel) return;

      summary.addEventListener('click', (event) => {
        event.preventDefault();
        details.open ? collapse(details, panel) : expand(details, panel);
      });
    });

    function expand(details, panel) {
      details.open = true;
      const height = panel.scrollHeight;
      panel.style.height = '0px';
      panel.style.opacity = '0';

      requestAnimationFrame(() => requestAnimationFrame(() => {
        panel.style.height = `${height}px`;
        panel.style.opacity = '1';
      }));

      panel.addEventListener('transitionend', function onEnd(e) {
        if (e.propertyName !== 'height') return;
        panel.style.removeProperty('height');
        panel.style.removeProperty('opacity');
        panel.removeEventListener('transitionend', onEnd);
      });
    }

    function collapse(details, panel) {
      panel.style.height = `${panel.scrollHeight}px`;
      panel.style.opacity = '1';

      requestAnimationFrame(() => requestAnimationFrame(() => {
        panel.style.height = '0px';
        panel.style.opacity = '0';
      }));

      panel.addEventListener('transitionend', function onEnd(e) {
        if (e.propertyName !== 'height') return;
        details.open = false;
        panel.style.removeProperty('height');
        panel.style.removeProperty('opacity');
        panel.removeEventListener('transitionend', onEnd);
      });
    }
  }

  /* ─────────────────────────────────────────────────────────────────
     Lightbox
     ───────────────────────────────────────────────────────────────── */
  function initLightbox(scope) {
    const dialog = scope.querySelector('.product-lightbox');
    if (!dialog || typeof dialog.showModal !== 'function') return;

    const triggers = scope.querySelectorAll('[data-gallery-trigger]');
    const slides = dialog.querySelectorAll('[data-slide-index]');
    const thumbs = dialog.querySelectorAll('[data-thumb-index]');
    const closeBtn = dialog.querySelector('[data-lightbox-close]');
    const prevBtn = dialog.querySelector('[data-lightbox-prev]');
    const nextBtn = dialog.querySelector('[data-lightbox-next]');

    if (!triggers.length || !slides.length) return;

    let currentIndex = 0;
    const total = slides.length;

    function setActive(index) {
      currentIndex = ((index % total) + total) % total;

      slides.forEach((slide, i) => {
        const isActive = i === currentIndex;
        slide.classList.toggle('is-active', isActive);
        slide.setAttribute('aria-hidden', isActive ? 'false' : 'true');
      });

      thumbs.forEach((thumb, i) => {
        const isActive = i === currentIndex;
        thumb.classList.toggle('is-active', isActive);
        thumb.setAttribute('aria-selected', isActive ? 'true' : 'false');
      });

      const activeThumb = thumbs[currentIndex];
      if (activeThumb) {
        activeThumb.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'center' });
      }
    }

    triggers.forEach((trigger) => {
      trigger.addEventListener('click', () => {
        const index = parseInt(trigger.dataset.index, 10) || 0;
        setActive(index);
        dialog.showModal();
      });
    });

    closeBtn && closeBtn.addEventListener('click', () => dialog.close());
    prevBtn && prevBtn.addEventListener('click', () => setActive(currentIndex - 1));
    nextBtn && nextBtn.addEventListener('click', () => setActive(currentIndex + 1));

    thumbs.forEach((thumb) => {
      thumb.addEventListener('click', () => {
        setActive(parseInt(thumb.dataset.thumbIndex, 10) || 0);
      });
    });

    dialog.addEventListener('keydown', (event) => {
      if (event.key === 'ArrowLeft') { event.preventDefault(); setActive(currentIndex - 1); }
      else if (event.key === 'ArrowRight') { event.preventDefault(); setActive(currentIndex + 1); }
    });

    const interactiveSelector = [
      '.product-lightbox__image',
      '.product-lightbox__arrow',
      '.product-lightbox__close',
      '.product-lightbox__thumbs',
    ].join(', ');

    dialog.addEventListener('click', (event) => {
      if (!event.target.closest(interactiveSelector)) dialog.close();
    });
  }

  /* ─────────────────────────────────────────────────────────────────
     Purchase options — switches selling_plan input + toggles
     subscribe vs one-time UI state
     ───────────────────────────────────────────────────────────────── */
  function initPurchaseOptions(scope) {
    const optionsWrap = scope.querySelector('[data-purchase-options]');
    if (!optionsWrap) return;

    const sellingPlanInput = scope.querySelector('[data-selling-plan-input]');
    const planSelect = scope.querySelector('[data-plan-select]');
    const sectionDefaultPlan = scope.dataset.sellingPlanId || '';
    const mainQtyInput = scope.querySelector('[data-main-qty-input]');

    function applyOption(value) {
      if (!sellingPlanInput) return;
      if (value === 'subscribe') {
        const planId = planSelect ? planSelect.value : sectionDefaultPlan;
        sellingPlanInput.value = planId || '';
        /* Subscription is always quantity 1 — qty selector is OTP-only */
        if (mainQtyInput) mainQtyInput.value = 1;
      } else {
        sellingPlanInput.value = '';
        /* If this OTP variant is already in the cart, mirror its line qty
           in the stepper so the user sees the current cart quantity. */
        if (mainQtyInput) {
          const variantId = scope.dataset.variantId;
          const line = CartState.getOneTime(variantId);
          mainQtyInput.value = line ? line.quantity : 1;
        }
      }
    }

    /* Initial state */
    const checked = optionsWrap.querySelector('input[name="purchase_option"]:checked');
    applyOption(checked ? checked.value : 'one-time');

    /* React to radio changes */
    optionsWrap.querySelectorAll('input[name="purchase_option"]').forEach((radio) => {
      radio.addEventListener('change', (event) => {
        if (event.target.checked) applyOption(event.target.value);
      });
    });

    /* React to frequency changes */
    if (planSelect) {
      planSelect.addEventListener('change', () => {
        const subscribeChecked = scope.querySelector(
          'input[name="purchase_option"][value="subscribe"]:checked'
        );
        if (subscribeChecked) applyOption('subscribe');
      });
    }

    /* Main qty stepper (only visible when OTP is selected, but always wired) */
    const mainQtyWrap = scope.querySelector('[data-main-qty]');
    const otpQtyMax = parseInt(scope.dataset.otpQtyMax, 10) || 0;

    function clampQtyValue() {
      if (!mainQtyInput) return;
      const min = parseInt(mainQtyInput.min, 10) || 1;
      let current = parseInt(mainQtyInput.value, 10);
      if (!(current > 0)) current = min;
      if (current < min) current = min;
      if (otpQtyMax > 0 && current > otpQtyMax) current = otpQtyMax;
      mainQtyInput.value = current;
    }

    function syncMainQtyDisabled() {
      if (!mainQtyWrap || !mainQtyInput) return;
      const current = parseInt(mainQtyInput.value, 10) || 1;
      const min = parseInt(mainQtyInput.min, 10) || 1;
      mainQtyWrap.classList.toggle('is-at-min', current <= min);
      mainQtyWrap.classList.toggle('is-at-max', otpQtyMax > 0 && current >= otpQtyMax);
    }

    if (mainQtyWrap) {
      mainQtyWrap.querySelectorAll('[data-qty-action]').forEach((btn) => {
        btn.addEventListener('click', () => {
          if (!mainQtyInput) return;
          const current = parseInt(mainQtyInput.value, 10) || 1;
          const min = parseInt(mainQtyInput.min, 10) || 1;
          let next = btn.dataset.qtyAction === 'increase'
            ? current + 1
            : Math.max(min, current - 1);
          if (otpQtyMax > 0 && next > otpQtyMax) next = otpQtyMax;
          mainQtyInput.value = next;
          syncMainQtyDisabled();
        });
      });
      if (mainQtyInput) {
        mainQtyInput.addEventListener('change', () => {
          clampQtyValue();
          syncMainQtyDisabled();
        });
      }
      syncMainQtyDisabled();
    }
  }

  /* ─────────────────────────────────────────────────────────────────
     Main Add-To-Cart — submits via AJAX, shows loading label,
     dispatches cart:updated so the header / future drawer can react
     ───────────────────────────────────────────────────────────────── */
  function initMainAtc(scope) {
    const form = scope.querySelector('.product-section__form');
    if (!form) return;

    const button = form.querySelector('[data-atc]');
    const labelEl = form.querySelector('[data-atc-label]');
    const variantId = scope.dataset.variantId;
    const mainQtyInput = scope.querySelector('[data-main-qty-input]');
    if (!button || !labelEl || !variantId) return;

    const idleLabel = labelEl.textContent.trim();
    /* Rendered disabled with a "Sold out" label when the variant is not
       available. syncState must leave it that way: it re-enables the
       button for every other state, which let a click add it anyway. */
    const soldOut = button.hasAttribute('data-sold-out');
    const loadingLabel = scope.dataset.atcLoading || 'Adding to my cart';
    const addedLabel = scope.dataset.atcAddedLabel || 'Added';
    const updateLabel = scope.dataset.atcUpdateLabel || 'Update cart';

    let isBusy = false;
    /* 'add' (default), 'added' (line in cart matches stepper qty),
       'update' (OTP line in cart but stepper qty differs),
       'blocked' (S&S already in cart). */
    let mode = 'add';

    function currentOption() {
      const checked = scope.querySelector('input[name="purchase_option"]:checked');
      return checked ? checked.value : 'one-time';
    }

    function readStepperQty() {
      const raw = parseInt(mainQtyInput && mainQtyInput.value, 10);
      return raw > 0 ? raw : 1;
    }

    function syncState() {
      if (isBusy || soldOut) return;

      if (currentOption() === 'subscribe') {
        const blocked = CartState.hasSubscription(variantId);
        mode = blocked ? 'blocked' : 'add';
        button.disabled = blocked;
        button.classList.toggle('is-added', blocked);
        button.classList.remove('is-update');
        labelEl.textContent = blocked ? addedLabel : idleLabel;
        return;
      }

      /* One-time path */
      const line = CartState.getOneTime(variantId);
      if (!line) {
        mode = 'add';
        button.disabled = false;
        button.classList.remove('is-added', 'is-update');
        labelEl.textContent = idleLabel;
        return;
      }

      if (readStepperQty() === line.quantity) {
        mode = 'added';
        button.disabled = true;
        button.classList.add('is-added');
        button.classList.remove('is-update');
        labelEl.textContent = addedLabel;
      } else {
        mode = 'update';
        button.disabled = false;
        button.classList.remove('is-added');
        button.classList.add('is-update');
        labelEl.textContent = updateLabel;
      }
    }

    /* React to option changes */
    scope.querySelectorAll('input[name="purchase_option"]').forEach((radio) => {
      radio.addEventListener('change', syncState);
    });

    /* React to stepper changes. The +/− buttons mutate the input
       synchronously in initPurchaseOptions' click handler (registered
       earlier in init order), so by the time these run the value is
       already current. */
    scope.querySelectorAll('[data-main-qty] [data-qty-action]').forEach((btn) => {
      btn.addEventListener('click', syncState);
    });
    if (mainQtyInput) {
      mainQtyInput.addEventListener('input', syncState);
      mainQtyInput.addEventListener('change', syncState);
    }

    /* React to cart updates. When OTP is the selected option and the
       variant is in cart, mirror the line qty into the stepper so the
       user sees the current cart state on page load and after every
       successful mutation (including external ones like cart drawer). */
    CartState.onChange(() => {
      if (!isBusy && currentOption() === 'one-time' && mainQtyInput) {
        const line = CartState.getOneTime(variantId);
        if (line) mainQtyInput.value = line.quantity;
      }
      syncState();
    });

    form.addEventListener('submit', async (event) => {
      event.preventDefault();
      if (button.disabled) return;

      const submitMode = mode;
      isBusy = true;
      button.disabled = true;
      labelEl.textContent = loadingLabel;

      try {
        if (submitMode === 'update') {
          /* Target the specific OTP line by its key — using variant_id
             would risk touching a co-existing S&S line of the same
             variant. */
          const line = CartState.getOneTime(variantId);
          if (!line) throw new Error('OTP line missing for update');
          const res = await fetch(`${window.Shopify.routes.root}cart/change.js`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
            body: JSON.stringify({ id: line.key, quantity: readStepperQty() }),
          });
          if (!res.ok) {
            const err = await res.json().catch(() => ({}));
            throw new Error(err.description || err.message || 'Cart update failed');
          }
        } else {
          const formData = new FormData(form);
          const res = await fetch(`${window.Shopify.routes.root}cart/add.js`, {
            method: 'POST',
            headers: { Accept: 'application/json' },
            body: formData,
          });
          if (!res.ok) {
            const err = await res.json().catch(() => ({}));
            throw new Error(err.description || err.message || 'Add to cart failed');
          }
        }

        await CartState.refresh();
        document.dispatchEvent(new CustomEvent('cart:updated', {
          detail: { source: 'product-section', form, mode: submitMode },
        }));
        if (window.WinonaCartDrawer) window.WinonaCartDrawer.open(button);
      } catch (err) {
        console.error(err);
        // TODO: surface this error to the user when we add toast UI
      } finally {
        isBusy = false;
        syncState();
      }
    });

    /* Initial state — mirror cart qty for OTP if applicable */
    if (currentOption() === 'one-time' && mainQtyInput) {
      const line = CartState.getOneTime(variantId);
      if (line) mainQtyInput.value = line.quantity;
    }
    syncState();
  }

  /* ─────────────────────────────────────────────────────────────────
     Frequently bought together
     - S&S / one-time radio per item
     - qty selector visible only on one-time
     - ADD button reflects cart state (ADD ⇄ ADDED disabled)
     ───────────────────────────────────────────────────────────────── */
  function initFbt(scope) {
    const items = scope.querySelectorAll('[data-fbt-item]');
    if (!items.length) return;

    const addedLabel = scope.dataset.fbtAddedLabel || 'Added';
    const loadingLabel = scope.dataset.fbtLoadingLabel || 'Adding';
    const updateLabel = scope.dataset.fbtUpdateLabel || 'Update cart';
    const otpQtyMax = parseInt(scope.dataset.otpQtyMax, 10) || 0;

    items.forEach((item) => {
      const variantId = item.dataset.variantId;
      const sellingPlanId = item.dataset.sellingPlanId || '';
      const qtyWrap = item.querySelector('[data-fbt-qty]');
      const qtyInput = item.querySelector('[data-fbt-qty-input]');
      const radios = item.querySelectorAll('[data-fbt-option]');
      const addBtn = item.querySelector('[data-fbt-add]');
      const addLabel = item.querySelector('[data-fbt-label]');
      if (!addBtn || !addLabel) return;

      const idleLabel = addLabel.textContent.trim();
      /* Sold out stays disabled, same as the main ATC. */
      const soldOut = addBtn.hasAttribute('data-sold-out');
      let isBusy = false;
      /* 'add' | 'added' | 'update' | 'blocked' — same machine as main ATC */
      let mode = 'add';

      function currentOption() {
        const checked = item.querySelector('[data-fbt-option]:checked');
        return checked ? checked.value : 'one-time';
      }

      function readQty() {
        const raw = parseInt(qtyInput && qtyInput.value, 10);
        return raw > 0 ? raw : 1;
      }

      function clampQtyValue() {
        if (!qtyInput) return;
        const min = parseInt(qtyInput.min, 10) || 1;
        let current = parseInt(qtyInput.value, 10);
        if (!(current > 0)) current = min;
        if (current < min) current = min;
        if (otpQtyMax > 0 && current > otpQtyMax) current = otpQtyMax;
        qtyInput.value = current;
      }

      function syncOption() {
        if (qtyWrap) qtyWrap.hidden = currentOption() !== 'one-time';
      }

      function syncFbtQtyDisabled() {
        if (!qtyWrap || !qtyInput) return;
        const current = parseInt(qtyInput.value, 10) || 1;
        const min = parseInt(qtyInput.min, 10) || 1;
        qtyWrap.classList.toggle('is-at-min', current <= min);
        qtyWrap.classList.toggle('is-at-max', otpQtyMax > 0 && current >= otpQtyMax);
      }

      function syncState() {
        if (isBusy || soldOut) return;

        if (currentOption() === 'subscribe') {
          const blocked = CartState.hasSubscription(variantId);
          mode = blocked ? 'blocked' : 'add';
          addBtn.disabled = blocked;
          addBtn.classList.toggle('is-added', blocked);
          addBtn.classList.remove('is-update');
          addLabel.textContent = blocked ? addedLabel : idleLabel;
          return;
        }

        /* One-time path */
        const line = CartState.getOneTime(variantId);
        if (!line) {
          mode = 'add';
          addBtn.disabled = false;
          addBtn.classList.remove('is-added', 'is-update');
          addLabel.textContent = idleLabel;
          return;
        }

        if (readQty() === line.quantity) {
          mode = 'added';
          addBtn.disabled = true;
          addBtn.classList.add('is-added');
          addBtn.classList.remove('is-update');
          addLabel.textContent = addedLabel;
        } else {
          mode = 'update';
          addBtn.disabled = false;
          addBtn.classList.remove('is-added');
          addBtn.classList.add('is-update');
          addLabel.textContent = updateLabel;
        }
      }

      radios.forEach((radio) => radio.addEventListener('change', () => {
        syncOption();
        /* Mirror cart qty when going back to one-time; subscribe always
           sends qty 1 (only one subscription allowed per product). */
        if (qtyInput) {
          if (currentOption() === 'one-time') {
            const line = CartState.getOneTime(variantId);
            qtyInput.value = line ? line.quantity : 1;
          } else {
            qtyInput.value = 1;
          }
        }
        syncFbtQtyDisabled();
        syncState();
      }));

      /* Qty stepper: registered after the dedicated +/- handlers below so
         the input value is current when syncState reads it. */
      item.querySelectorAll('[data-qty-action]').forEach((btn) => {
        btn.addEventListener('click', () => {
          if (!qtyInput) return;
          const current = parseInt(qtyInput.value, 10) || 1;
          const min = parseInt(qtyInput.min, 10) || 1;
          let next = btn.dataset.qtyAction === 'increase'
            ? current + 1
            : Math.max(min, current - 1);
          if (otpQtyMax > 0 && next > otpQtyMax) next = otpQtyMax;
          qtyInput.value = next;
          syncFbtQtyDisabled();
          syncState();
        });
      });
      if (qtyInput) {
        qtyInput.addEventListener('input', syncState);
        qtyInput.addEventListener('change', () => {
          clampQtyValue();
          syncFbtQtyDisabled();
          syncState();
        });
      }
      syncFbtQtyDisabled();

      addBtn.addEventListener('click', async () => {
        if (addBtn.disabled || isBusy) return;

        const submitMode = mode;
        isBusy = true;
        addBtn.disabled = true;
        addLabel.textContent = loadingLabel;

        try {
          if (submitMode === 'update') {
            const line = CartState.getOneTime(variantId);
            if (!line) throw new Error('OTP line missing for FBT update');
            const res = await fetch(`${window.Shopify.routes.root}cart/change.js`, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
              body: JSON.stringify({ id: line.key, quantity: readQty() }),
            });
            if (!res.ok) {
              const err = await res.json().catch(() => ({}));
              throw new Error(err.description || err.message || 'FBT update failed');
            }
          } else {
            const option = currentOption();
            const quantity = option === 'one-time' ? readQty() : 1;
            const payload = {
              items: [{
                id: parseInt(variantId, 10),
                quantity,
                ...(option === 'subscribe' && sellingPlanId
                  ? { selling_plan: parseInt(sellingPlanId, 10) }
                  : {}),
              }],
            };
            const res = await fetch(`${window.Shopify.routes.root}cart/add.js`, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
              body: JSON.stringify(payload),
            });
            if (!res.ok) {
              const err = await res.json().catch(() => ({}));
              throw new Error(err.description || err.message || 'FBT add failed');
            }
          }

          await CartState.refresh();
          document.dispatchEvent(new CustomEvent('cart:updated', {
            detail: { source: 'fbt', variantId, mode: submitMode },
          }));
          if (window.WinonaCartDrawer) window.WinonaCartDrawer.open(addBtn);
        } catch (err) {
          console.error('FBT add failed', err);
        } finally {
          isBusy = false;
          syncState();
        }
      });

      CartState.onChange(() => {
        if (!isBusy && currentOption() === 'one-time' && qtyInput) {
          const line = CartState.getOneTime(variantId);
          if (line) {
            qtyInput.value = line.quantity;
            syncFbtQtyDisabled();
          }
        }
        syncState();
      });

      /* Initial state — if OTP is selected and in cart, mirror line qty */
      if (currentOption() === 'one-time' && qtyInput) {
        const line = CartState.getOneTime(variantId);
        if (line) qtyInput.value = line.quantity;
      }
      syncOption();
      syncFbtQtyDisabled();
      syncState();
    });
  }

  /* ─────────────────────────────────────────────────────────────────
     Frequently bought together — Splide slider (tablet & mobile only).
     We mount Splide when the viewport drops under 1280px and destroy
     it when we go back to desktop, so each layout gets the markup it
     expects (stacked column at desktop, 1-slide carousel below).
     ───────────────────────────────────────────────────────────────── */
  function initBundleSlider(scope) {
    const sliderEl = scope.querySelector('[data-product-bundle]');
    if (!sliderEl) return;

    const slideCount = sliderEl.querySelectorAll('.splide__slide').length;
    if (slideCount <= 1) return;

    const tabletMQ = window.matchMedia('(max-width: 1279.98px)');
    let splideInstance = null;

    function mount() {
      if (splideInstance) return;
      if (typeof window.Splide !== 'function') return;
      try {
        const splide = new window.Splide(sliderEl, {
          perPage: 1,
          perMove: 1,
          arrows: true,
          pagination: false,
          drag: true,
          speed: 400,
          gap: 0,
          fixedWidth: undefined,
          autoWidth: false,
        });
        splide.on('mounted', () => buildBundlePagination(splide, sliderEl));
        splide.on('updated resized', () => buildBundlePagination(splide, sliderEl));
        splide.on('move', (newIndex) => updateBundleActiveDot(sliderEl, newIndex));
        splide.mount();
        splideInstance = splide;
      } catch (err) {
        console.error('Bundle Splide init failed:', err);
      }
    }

    function unmount() {
      if (!splideInstance) return;
      try {
        splideInstance.destroy(true);
      } catch (err) {
        console.error('Bundle Splide destroy failed:', err);
      }
      splideInstance = null;
      // Clear any leftover pagination dots from the previous mount.
      const dots = sliderEl.querySelector('.product-section__bundle-pagination');
      if (dots) dots.remove();
    }

    function sync() {
      if (tabletMQ.matches) mount();
      else unmount();
    }

    function ready() {
      sync();
      // modern browsers
      if (typeof tabletMQ.addEventListener === 'function') {
        tabletMQ.addEventListener('change', sync);
      } else if (typeof tabletMQ.addListener === 'function') {
        tabletMQ.addListener(sync);
      }
    }

    if (typeof window.Splide === 'function') {
      ready();
    } else {
      const onReady = () => {
        document.removeEventListener('splide:ready', onReady);
        ready();
      };
      document.addEventListener('splide:ready', onReady);
      // Guard against the splide:ready event having fired before this
      // listener was attached — poll briefly until Splide is available.
      let attempts = 0;
      const poll = setInterval(() => {
        attempts += 1;
        if (typeof window.Splide === 'function') {
          clearInterval(poll);
          document.removeEventListener('splide:ready', onReady);
          ready();
        } else if (attempts > 50) {
          clearInterval(poll); // give up after ~5s
        }
      }, 100);
    }
  }

  function ensureBundlePagination(sliderEl) {
    let ul = sliderEl.querySelector('.product-section__bundle-pagination');
    if (ul) return ul;

    ul = document.createElement('ul');
    ul.className = 'product-section__bundle-pagination';
    ul.setAttribute('aria-label', 'Bundle pagination');

    const nav = sliderEl.querySelector('.product-section__bundle-nav');
    const nextBtn = nav && nav.querySelector('.product-section__bundle-arrow--next');
    if (nav && nextBtn) {
      nav.insertBefore(ul, nextBtn);
    } else if (nav) {
      nav.appendChild(ul);
    } else {
      sliderEl.appendChild(ul);
    }
    return ul;
  }

  function buildBundlePagination(splide, sliderEl) {
    const ul = ensureBundlePagination(sliderEl);
    if (!ul) return;

    const length = (typeof splide.length === 'number' && splide.length > 0)
      ? splide.length
      : sliderEl.querySelectorAll('.splide__slide').length;
    const activeIndex = typeof splide.index === 'number' ? splide.index : 0;

    while (ul.firstChild) ul.removeChild(ul.firstChild);

    if (length <= 1) {
      ul.hidden = true;
      return;
    }
    ul.hidden = false;

    for (let i = 0; i < length; i++) {
      (function (idx) {
        const li = document.createElement('li');
        const btn = document.createElement('button');
        btn.type = 'button';
        btn.className = 'product-section__bundle-page';
        btn.setAttribute('aria-label', 'Go to product ' + (idx + 1));
        if (idx === activeIndex) btn.classList.add('is-active');
        btn.addEventListener('click', () => splide.go(idx));
        li.appendChild(btn);
        ul.appendChild(li);
      })(i);
    }
  }

  function updateBundleActiveDot(sliderEl, index) {
    sliderEl.querySelectorAll('.product-section__bundle-page').forEach((btn, i) => {
      btn.classList.toggle('is-active', i === index);
    });
  }

  /* ─────────────────────────────────────────────────────────────────
     Gallery — Splide slider on mobile only (dots + swipe, no arrows).
     Same mount/unmount pattern as the FBT bundle: Splide only runs
     under 768px; above that the multi-tile CSS layout takes over.

     On mobile the gallery DOM also gets relocated into the header card
     (between the heading group and the short description) so the
     layout matches Figma 1490:21508. On tablet/desktop it sits back
     in the left grid cell of .product-section__inner.
     ───────────────────────────────────────────────────────────────── */
  function initGallerySlider(scope) {
    const sliderEl = scope.querySelector('[data-product-gallery-slider]');
    if (!sliderEl) return;

    const galleryEl = scope.querySelector('[data-product-gallery]');
    const inner = scope.querySelector('.product-section__inner');
    const headerText = scope.querySelector('.product-section__header-text');
    /* The gallery is inserted after whichever element carries
       data-gallery-anchor: the heading group on Supplements, or the
       tag-pills + heading wrapper on Skincare, where the pills sit above
       the title and must stay above the image. */
    const heading =
      scope.querySelector('[data-gallery-anchor]') ||
      scope.querySelector('.product-section__heading');

    const slideCount = sliderEl.querySelectorAll('.splide__slide').length;

    const mobileMQ = window.matchMedia('(max-width: 767.98px)');
    let splideInstance = null;

    function syncPosition() {
      if (!galleryEl || !inner) return;
      if (mobileMQ.matches) {
        if (headerText && heading && galleryEl.parentElement !== headerText) {
          heading.insertAdjacentElement('afterend', galleryEl);
        }
      } else if (galleryEl.parentElement !== inner) {
        inner.insertBefore(galleryEl, inner.firstChild);
      }
    }

    function mount() {
      if (splideInstance) return;
      if (typeof window.Splide !== 'function') return;
      if (slideCount < 1) return;
      /* Mount even for a single image. Splide's own CSS hides slides with
         `visibility: hidden` until it marks the track as rendered, so
         skipping the mount left one-image galleries invisible on mobile.
         buildGalleryPagination() already hides the dots below 2 slides. */
      try {
        const splide = new window.Splide(sliderEl, {
          perPage: 1,
          perMove: 1,
          arrows: false,
          pagination: false,
          drag: slideCount > 1,
          speed: 400,
          gap: 0,
          autoWidth: false,
        });
        splide.on('mounted', () => buildGalleryPagination(splide, sliderEl));
        splide.on('updated resized', () => buildGalleryPagination(splide, sliderEl));
        splide.on('move', (newIndex) => updateGalleryActiveDot(sliderEl, newIndex));
        splide.mount();
        splideInstance = splide;
      } catch (err) {
        console.error('Gallery Splide init failed:', err);
      }
    }

    function unmount() {
      if (!splideInstance) return;
      try {
        splideInstance.destroy(true);
      } catch (err) {
        console.error('Gallery Splide destroy failed:', err);
      }
      splideInstance = null;
      const dots = sliderEl.querySelector('.product-gallery__pagination');
      if (dots) dots.remove();
    }

    function sync() {
      if (mobileMQ.matches) {
        /* Move into the header card BEFORE mounting so Splide measures
           the slider in its final position. */
        syncPosition();
        mount();
      } else {
        /* Destroy Splide first so it cleans up against its current DOM,
           then relocate the gallery back to the grid's left cell. */
        unmount();
        syncPosition();
      }
    }

    function ready() {
      sync();
      if (typeof mobileMQ.addEventListener === 'function') {
        mobileMQ.addEventListener('change', sync);
      } else if (typeof mobileMQ.addListener === 'function') {
        mobileMQ.addListener(sync);
      }
    }

    if (typeof window.Splide === 'function') {
      ready();
    } else {
      const onReady = () => {
        document.removeEventListener('splide:ready', onReady);
        ready();
      };
      document.addEventListener('splide:ready', onReady);
      let attempts = 0;
      const poll = setInterval(() => {
        attempts += 1;
        if (typeof window.Splide === 'function') {
          clearInterval(poll);
          document.removeEventListener('splide:ready', onReady);
          ready();
        } else if (attempts > 50) {
          clearInterval(poll);
        }
      }, 100);
    }
  }

  function ensureGalleryPagination(sliderEl) {
    let ul = sliderEl.querySelector('.product-gallery__pagination');
    if (ul) return ul;

    ul = document.createElement('ul');
    ul.className = 'product-gallery__pagination';
    ul.setAttribute('aria-label', 'Gallery pagination');
    sliderEl.appendChild(ul);
    return ul;
  }

  function buildGalleryPagination(splide, sliderEl) {
    const ul = ensureGalleryPagination(sliderEl);
    if (!ul) return;

    const length = (typeof splide.length === 'number' && splide.length > 0)
      ? splide.length
      : sliderEl.querySelectorAll('.splide__slide').length;
    const activeIndex = typeof splide.index === 'number' ? splide.index : 0;

    while (ul.firstChild) ul.removeChild(ul.firstChild);

    if (length <= 1) {
      ul.hidden = true;
      return;
    }
    ul.hidden = false;

    for (let i = 0; i < length; i++) {
      (function (idx) {
        const li = document.createElement('li');
        const btn = document.createElement('button');
        btn.type = 'button';
        btn.className = 'product-gallery__page';
        btn.setAttribute('aria-label', 'Go to image ' + (idx + 1));
        if (idx === activeIndex) btn.classList.add('is-active');
        btn.addEventListener('click', () => splide.go(idx));
        li.appendChild(btn);
        ul.appendChild(li);
      })(i);
    }
  }

  function updateGalleryActiveDot(sliderEl, index) {
    sliderEl.querySelectorAll('.product-gallery__page').forEach((btn, i) => {
      btn.classList.toggle('is-active', i === index);
    });
  }

  /* ─────────────────────────────────────────────────────────────────
     Mobile sticky ATC — toggled via IntersectionObserver on the main
     in-page ATC. Tap scrolls smoothly to the purchase card, offsetting
     for the sticky site header so the radios are visible.
     ───────────────────────────────────────────────────────────────── */
  function initStickyAtc(scope) {
    const sticky = scope.querySelector('[data-sticky-atc]');
    const mainAtc = scope.querySelector('[data-atc]');
    const trigger = scope.querySelector('[data-sticky-atc-trigger]');
    const purchaseCard = scope.querySelector('.product-section__card--purchase');
    const footer = document.querySelector('.footer');
    if (!sticky || !mainAtc || !trigger || !purchaseCard) return;

    /* Show the sticky bar only when the in-page ATC has scrolled out AND the
       footer hasn't reached the viewport yet — otherwise the bar would sit
       on top of the footer. */
    let mainAtcVisible = true;
    let footerVisible = false;
    const updateVisibility = () => {
      sticky.classList.toggle('is-visible', !mainAtcVisible && !footerVisible);
    };

    if ('IntersectionObserver' in window) {
      new IntersectionObserver(
        (entries) => {
          mainAtcVisible = entries[0].isIntersecting;
          updateVisibility();
        },
        { threshold: 0 }
      ).observe(mainAtc);

      if (footer) {
        new IntersectionObserver(
          (entries) => {
            footerVisible = entries[0].isIntersecting;
            updateVisibility();
          },
          { threshold: 0 }
        ).observe(footer);
      }
    }

    trigger.addEventListener('click', () => {
      const header = document.querySelector('.site-header');
      const headerHeight = header ? header.offsetHeight : 0;
      const targetY =
        window.pageYOffset + purchaseCard.getBoundingClientRect().top - headerHeight - 16;
      window.scrollTo({ top: Math.max(0, targetY), behavior: 'smooth' });
    });
  }
})();
